import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { brand } from '../config/index.js'
import {
  getDefaultVariant,
  isVariantAddToCartDisabled,
  resolveVariants,
  stockAlertLabel,
} from '../config/productUtils.js'
import { useProducts } from '../context/ProductsContext.jsx'
import { useCart } from '../context/CartContext.jsx'
import SEO from '../components/SEO.jsx'
import { getPeptideSeo, applySeoOverrides } from '../config/seo.js'
import Button from '../components/Button.jsx'
import Badge from '../components/Badge.jsx'
import ProductVisual from '../components/ProductVisual.jsx'
import ProductVariantSizeChips from '../components/ProductVariantSizeChips.jsx'
import WishlistButton from '../components/WishlistButton.jsx'
import './ProductDetailPage.css'

export default function ProductDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { getProductById, loading, inventory, saleBadgeLabel, showCompareAtPrice, seoSettings, preorderMessage } = useProducts()
  const { addItem, openCart } = useCart()

  const product = useMemo(() => (id ? getProductById(id) : null), [id, getProductById])
  const variants = useMemo(() => (product ? resolveVariants(product) : []), [product])
  const [variantId, setVariantId] = useState(null)
  const [qty, setQty] = useState(1)

  useEffect(() => {
    const p = id ? getProductById(id) : null
    if (!p) return
    setVariantId(getDefaultVariant(p).id)
  }, [id, getProductById])

  useEffect(() => {
    if (!loading && !product) navigate('/catalog', { replace: true })
  }, [loading, product, navigate])

  if (loading || !product) {
    return <div className="container product-detail__loading">Loading&hellip;</div>
  }

  const selected = variants.find((v) => v.id === variantId) ?? variants[0]
  const saleLabel = saleBadgeLabel()
  const stockLabel = stockAlertLabel(product, selected, inventory)
  const addToCartDisabled = isVariantAddToCartDisabled(product, selected, inventory)
  const showCompare = showCompareAtPrice(selected)
  const sections = product.detail?.sections ?? []
  const isPreorder = variants.some((v) => inventory?.[`${product.id}__${v.id}`]?.preorder)

  // Preorder overrides all stock-based disabling and messaging
  const effectiveAddToCartDisabled = isPreorder ? false : addToCartDisabled
  const effectiveStockLabel        = isPreorder ? null  : stockLabel

  const seoConfig = applySeoOverrides(getPeptideSeo(product), seoSettings?.products?.[id])

  return (
    <div className="container product-detail">
      <SEO config={seoConfig} />

      <Link to="/catalog" className="product-detail__back">← Back to catalog</Link>

      <div className="product-detail__grid">
        <div className="product-detail__visual">
          <ProductVisual product={product} concentration={selected.concentration} labelSize={selected.labelSize} />
        </div>

        <motion.div
          className="product-detail__info"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="product-detail__badges">
            {product.badge && (
              <Badge style={{ color: brand.colors.accent, borderColor: brand.colors.borderHover }}>{product.badge}</Badge>
            )}
            {saleLabel && (
              <Badge style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>{saleLabel}</Badge>
            )}
            {isPreorder && (
              <Badge style={{ color: 'var(--warning)', borderColor: 'var(--warning)' }}>Pre-order</Badge>
            )}
            {!isPreorder && effectiveStockLabel && (
              <Badge style={{ color: 'var(--warning)', borderColor: 'var(--warning)' }}>{effectiveStockLabel}</Badge>
            )}
          </div>

          <h1>{product.name}</h1>
          <p className="product-detail__subtitle">{product.subtitle}</p>
          <p className="product-detail__summary">{product.summary}</p>

          <ProductVariantSizeChips
            product={product}
            selectedId={variantId ?? selected.id}
            onSelectId={setVariantId}
            inventory={inventory}
            productId={product.id}
          />

          <p className="product-detail__concentration">{selected.concentration}</p>

          <div className="product-detail__price">
            {showCompare && <s>${selected.compareAtPrice}</s>}
            <span>${selected.price}</span>
          </div>

          {isPreorder && (
            <div className="product-detail__preorder-banner">
              <strong>Pre-order</strong>
              <p>{preorderMessage}</p>
            </div>
          )}

          {product.benefits?.length > 0 && (
            <ul className="product-detail__benefits">
              {product.benefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}

          <div className="product-detail__qty-row">
            <div className="product-detail__qty">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">
                &minus;
              </button>
              <span>{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity">
                +
              </button>
            </div>
            <Button
              variant="primary"
              disabled={effectiveAddToCartDisabled}
              onClick={effectiveAddToCartDisabled ? undefined : () => { addItem(product, selected, qty); openCart() }}
            >
              {isPreorder ? 'Pre-order' : 'Add to cart'} &mdash; ${(selected.price * qty).toFixed(2)}
            </Button>
            <WishlistButton productId={product.id} variantId={selected.id} />
          </div>

          {!isPreorder && stockLabel && <div className="product-detail__stock">{stockLabel}</div>}

          <div className="product-detail__disclaimer">
            <p className="product-detail__disclaimer-title">Research use only</p>
            <p>
              This compound is sold for in-vitro research purposes only. Not approved by the FDA. Not intended
              for human use, consumption, or administration. Not intended to diagnose, treat, cure, or prevent
              any disease.
            </p>
          </div>
        </motion.div>
      </div>

      {sections.length > 0 && (
        <div className="product-detail__sections">
          <h2>Deep dive</h2>
          <div className="product-detail__sections-grid">
            {sections.map((block) => (
              <section key={block.title}>
                <h3>{block.title}</h3>
                <p>{block.body}</p>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
